import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService, Product } from '../services/api';
import { useCart } from '../context/CartContext';
import QuickAddToCartModal from './QuickAddToCartModal';

const PromotionSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoProducts, setPromoProducts] = useState<Product[]>([]);
  const { addToCart } = useCart();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddProduct, setQuickAddProduct] = useState<Product | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.getProducts();
        const items = res?.data || [];
        // Identify promotional products from known fields
        const promos = items.filter((p: Product) =>
          p.has_promotion === true ||
          typeof p.discounted_price === 'number' ||
          typeof p.promotional_price === 'number' ||
          (Array.isArray(p.active_promotions) && p.active_promotions.length > 0)
        );
        // Sort latest by promotional timestamp fallback to updated_at/created_at
        const sorted = promos.sort((a, b) => {
          const aDate = new Date(a.promotional_price_set_at || a.updated_at || a.created_at || 0).getTime();
          const bDate = new Date(b.promotional_price_set_at || b.updated_at || b.created_at || 0).getTime();
          return bDate - aDate;
        });
        if (mounted) setPromoProducts(sorted.slice(0, 4));
      } catch (e: any) {
        if (mounted) setError('Failed to load promotion items');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const hasData = promoProducts.length > 0;

  // Hide section if no data
  if (loading || error || !hasData) {
    // Show skeleton during loading to prevent CLS
    if (loading) {
      return (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="h-4 w-32 bg-gray-200 rounded mx-auto mb-2 animate-pulse"></div>
              <div className="h-8 w-48 bg-gray-200 rounded mx-auto mb-4 animate-pulse"></div>
              <div className="h-4 w-64 bg-gray-200 rounded mx-auto animate-pulse"></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                  <div className="w-full h-48 sm:h-80 bg-gray-200 animate-pulse"></div>
                  <div className="p-3 sm:p-4">
                    <div className="h-4 bg-gray-200 rounded mb-2 animate-pulse"></div>
                    <div className="h-6 w-32 bg-gray-200 rounded animate-pulse"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-block">
            <span className="text-sm font-medium text-gray-500 tracking-wider uppercase mb-2 block">Signature Style</span>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Promotion Item</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Limited-Time Promotion - Shop Now!</p>
          </div>
        </div>

        {/* Promotional Products Grid - Matching Other Sections */}
        <div className="mb-12">
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {/* Dynamic promotional items */}
            {promoProducts.map((p) => {
              const primaryImg = p.product_images?.[0];
              const hoverImg = p.hover_image || p.product_images?.[1];
              
              return (
                <div key={p.id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden group">
                  <div className="relative overflow-hidden">
                    <Link to={`/products/${p.slug}`} className="block">
                      {primaryImg && (
                        <img
                          loading="lazy"
                          width="400"
                          height="400"
                          src={primaryImg}
                          alt={p.name}
                          className="w-full h-48 sm:h-80 object-cover transition-opacity duration-500 group-hover:opacity-0"
                        />
                      )}
                      {hoverImg && (
                        <img
                          loading="lazy"
                          width="400"
                          height="400"
                          src={hoverImg}
                          alt={`${p.name} alt`}
                          className="absolute inset-0 w-full h-48 sm:h-80 object-cover transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                        />
                      )}
                      
                      {(p.has_promotion || (p.promotional_price && p.promotional_price < p.price) || (p.discounted_price && p.discounted_price < p.price)) && (
                        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 z-10">
                          <span className="bg-orange-500 text-white px-1.5 py-0.5 sm:px-2 sm:py-0.5 text-[10px] sm:text-xs font-semibold rounded shadow">
                            {p.discount_percentage ? `-${Math.round(p.discount_percentage)}%` : 'SALE'}
                          </span>
                        </div>
                      )}
                    </Link>

                    {/* Action Buttons */}
                    <div className="absolute bottom-2 sm:bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10">
                      <button
                        className="w-8 h-8 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors shadow-lg"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (p.variants && p.variants.length > 0) {
                            setQuickAddProduct(p);
                            setQuickAddOpen(true);
                          } else {
                            addToCart(p);
                          }
                        }}
                        aria-label="Add to cart"
                        title="Add to cart"
                      >
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.5 6M7 13l-1.5-6m0 0h12.5M17 21a2 2 0 100-4 2 2 0 000 4zM9 21a2 2 0 100-4 2 2 0 000 4z" />
                        </svg>
                      </button>
                      <Link
                        to={`/products/${p.slug}`}
                        className="w-8 h-8 sm:w-12 sm:h-12 bg-gray-600 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors shadow-lg"
                        aria-label="View details"
                        title="View details"
                      >
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                  <div className="p-3 sm:p-4">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-800 mb-2 line-clamp-2">
                      {p.name}
                    </h3>
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <span className="text-sm sm:text-lg font-bold text-gray-900">
                        {(p.has_promotion || (p.promotional_price && p.promotional_price < p.price))
                          ? Number(p.promotional_price || p.price).toLocaleString("en-US")
                          : (p.discounted_price && p.discounted_price < p.price)
                          ? Number(p.discounted_price || p.price).toLocaleString("en-US")
                          : Number(p.price).toLocaleString("en-US")
                        } Kyats
                      </span>
                      {((p.has_promotion || (p.promotional_price && p.promotional_price < p.price)) || (p.discounted_price && p.discounted_price < p.price)) && (
                        <span className="text-xs sm:text-sm text-gray-500 line-through">{Number(p.price).toLocaleString("en-US")} Kyats</span>
                      )}
                      {!p.has_promotion && !p.promotional_price && !p.discounted_price && p.compare_at_price && p.compare_at_price > p.price && (
                        <span className="text-xs sm:text-sm text-gray-500 line-through">{Number(p.compare_at_price).toLocaleString("en-US")} Kyats</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            to="/product-signature?promotions=1"
            className="inline-flex items-center bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            View All
            <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>

      {/* Quick Add To Cart Modal */}
      <QuickAddToCartModal
        isOpen={quickAddOpen}
        product={quickAddProduct}
        onClose={() => { setQuickAddOpen(false); setQuickAddProduct(null); }}
        onConfirm={(sel) => {
          if (!quickAddProduct) return;
          addToCart(quickAddProduct, sel.quantity, {
            variantId: sel.variantId,
            sizeName: sel.sizeName,
            unitPrice: sel.unitPrice,
            compareAtPrice: sel.compareAtPrice,
            inStock: sel.inStock,
            colorName: sel.colorName,
            colorCode: sel.colorCode,
          });
          setQuickAddOpen(false);
          setQuickAddProduct(null);
        }}
      />
    </section>
  );
};

export default PromotionSection;
